import * as React from 'react';
import { withStyles, WithStyles, createStyles } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';

import { withIntl, IntlProps } from 'ha/i18n';

import {
  CanonicalFurniture,
  CanonicalBills,
  CanonicalRegistration,
} from 'ha/constants/Filters';
import * as SearchFilters from 'ha/types/SearchFilters';

import { priceRange } from 'ha/utils/formatRanges';
import Row from 'ha/ui/next/Row';
import units from 'ha/theme/units';

const styles = createStyles({
  row: {
    padding: units.yx(0, 1),
  },
  chip: {
    margin: units.trbl(0.25, 0.25, 0, 0.25),
  },
});

interface Props {
  types: SearchFilters.TypeFilterValue;
  furniture: SearchFilters.FurnitureFilterValue;
  bills: SearchFilters.BillsFilterValue;
  registration: SearchFilters.RegistrationFilterValue;
  suitableFor: SearchFilters.SuitableForFilterValue;
  recentlyAdded: SearchFilters.RecentlyAddedFilterValue;
  rules: SearchFilters.RulesFilterValue;
  gender: SearchFilters.GenderFilterValue;
  rooms: SearchFilters.RoomsFilterValue;
  rate: number;
  price: SearchFilters.PriceFilterChange | null;
  onRemove: (filter: string, value?: string) => void;
  disabledFilters: string[];
}

export const MobileFiltersTags: React.SFC<
  Props & IntlProps & WithStyles<typeof styles>
> = ({
  classes,
  types,
  furniture,
  bills,
  registration,
  rules,
  suitableFor,
  recentlyAdded,
  gender,
  rooms,
  price,
  rate,
  onRemove,
  disabledFilters,
  intl: { T },
}) => {
  const areFiltersApplied =
    price ||
    types.length > 0 ||
    furniture ||
    bills ||
    registration ||
    recentlyAdded ||
    rooms.bedroomCount > 0 ||
    rules.length > 0 ||
    gender.length > 0 ||
    suitableFor.length > 0 ||
    rooms.bedroomCount > 0;

  if (!areFiltersApplied) {
    return null;
  }

  return (
    <React.Fragment>
      <hr />
      <div>
        <Row className={classes.row}>
          {types.includes(SearchFilters.TypeValue.APARTMENT) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('type', SearchFilters.TypeValue.APARTMENT)
              }
              label={T('Apartment')}
            />
          )}

          {types.includes(SearchFilters.TypeValue.PRIVATE_ROOM) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('type', SearchFilters.TypeValue.PRIVATE_ROOM)
              }
              label={T('Private Room')}
            />
          )}

          {types.includes(SearchFilters.TypeValue.SHARED_ROOM) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('type', SearchFilters.TypeValue.SHARED_ROOM)
              }
              label={T('Shared Room')}
            />
          )}

          {types.includes(SearchFilters.TypeValue.STUDIO) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('type', SearchFilters.TypeValue.STUDIO)}
              label={T('Studio')}
            />
          )}

          {furniture && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('furniture')}
              label={
                {
                  [CanonicalFurniture.Furnished]: T('Furnished'),
                  [CanonicalFurniture.Unfurnished]: T('Unfurnished'),
                }[furniture]
              }
            />
          )}

          {bills && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('bills')}
              label={
                {
                  [CanonicalBills.Included]: T('Bills Included'),
                  [CanonicalBills.Excluded]: T('Bills Excluded'),
                }[bills]
              }
            />
          )}

          {suitableFor.includes(SearchFilters.SuitableForValue.Students) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('suitableFor', SearchFilters.SuitableForValue.Students)
              }
              label={T('Suitable for students')}
            />
          )}

          {suitableFor.includes(
            SearchFilters.SuitableForValue.WorkingProfessionals,
          ) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove(
                  'suitableFor',
                  SearchFilters.SuitableForValue.WorkingProfessionals,
                )
              }
              label={T('Suitable for working professionals')}
            />
          )}

          {suitableFor.includes(SearchFilters.SuitableForValue.Couples) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('suitableFor', SearchFilters.SuitableForValue.Couples)
              }
              label={T('Suitable for couples')}
            />
          )}

          {!disabledFilters.includes('rooms') && rooms.bedroomCount > 0 && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('bedroomCount')}
              label={T('%s+ bedrooms', rooms.bedroomCount)}
            />
          )}

          {registration && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('registration')}
              label={
                {
                  [CanonicalRegistration.Possible]: T('Registration Possible'),
                  [CanonicalRegistration.NotPossible]: T(
                    'Registration Not Possible',
                  ),
                }[registration]
              }
            />
          )}

          {gender.includes(SearchFilters.GenderValue.BOYS) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('gender', SearchFilters.GenderValue.BOYS)
              }
              label={T('Shared with Male')}
            />
          )}

          {gender.includes(SearchFilters.GenderValue.GIRLS) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('gender', SearchFilters.GenderValue.GIRLS)
              }
              label={T('Shared with Female')}
            />
          )}

          {rules.includes(SearchFilters.RulesValue.SMOKING) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('rules', SearchFilters.RulesValue.SMOKING)
              }
              label={T('Smoking Allowed')}
            />
          )}

          {rules.includes(SearchFilters.RulesValue.PETS) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('rules', SearchFilters.RulesValue.PETS)}
              label={T('Pets Allowed')}
            />
          )}

          {rules.includes(SearchFilters.RulesValue.INSTRUMENT) && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() =>
                onRemove('rules', SearchFilters.RulesValue.INSTRUMENT)
              }
              label={T('Playing Instrument Allowed')}
            />
          )}

          {price && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('price')}
              label={priceRange(
                price.currency,
                price.min * rate,
                price.max * rate,
              )}
            />
          )}

          {recentlyAdded && (
            <Chip
              className={classes.chip}
              color="primary"
              onDelete={() => onRemove('recentlyAdded')}
              label={
                {
                  '1-day': T('Last 24 hours'),
                  '3-days': T('Last 3 days'),
                  '7-days': T('Last 7 days'),
                  '14-days': T('Last 14 days'),
                }[recentlyAdded]
              }
            />
          )}
        </Row>
      </div>
    </React.Fragment>
  );
};

export default withIntl(withStyles(styles)(MobileFiltersTags));
